import type { IconNameExtended } from "@/components/library/Icon";

export const AVAILABLE_LIGHT_FILE_ICONS = [
  "file",
  "file-arrow-down",
  "file-arrow-up",
  "file-audio",
  "file-binary",
  "file-circle-check",
  "file-circle-exclamation",
  "file-circle-plus",
  "file-code",
  "file-contract",
  "file-csv",
  "file-doc",
  "file-excel",
  "file-exclamation",
  "file-export",
  "file-gif",
  "file-image",
  "file-import",
  "file-jpg",
  "file-lines",
  "file-magnifying-glass",
  "file-mp3",
  "file-pdf",
  "file-pen",
  "file-png",
  "file-powerpoint",
  "file-ppt",
  "file-signature",
  "file-svg",
  "file-user",
  "file-word",
  "file-xls",
  "file-xml",
  "file-zip",
] as const satisfies readonly IconNameExtended[];

export type AvailableLightFileIconName = (typeof AVAILABLE_LIGHT_FILE_ICONS)[number];

export type FileIconKind =
  | "audio"
  | "binary"
  | "code"
  | "csv"
  | "default"
  | "excel"
  | "gif"
  | "image"
  | "jpg"
  | "pdf"
  | "png"
  | "powerpoint"
  | "svg"
  | "text"
  | "video"
  | "word"
  | "xml"
  | "zip";

export type FileIconName = Extract<
  AvailableLightFileIconName,
  | "file"
  | "file-audio"
  | "file-binary"
  | "file-code"
  | "file-csv"
  | "file-doc"
  | "file-excel"
  | "file-gif"
  | "file-image"
  | "file-jpg"
  | "file-lines"
  | "file-pdf"
  | "file-png"
  | "file-powerpoint"
  | "file-svg"
  | "file-xml"
  | "file-zip"
>;

export const FILE_ICON_BY_KIND = {
  audio: "file-audio",
  binary: "file-binary",
  code: "file-code",
  csv: "file-csv",
  default: "file",
  excel: "file-excel",
  gif: "file-gif",
  image: "file-image",
  jpg: "file-jpg",
  pdf: "file-pdf",
  png: "file-png",
  powerpoint: "file-powerpoint",
  svg: "file-svg",
  text: "file-lines",
  video: "file-binary",
  word: "file-doc",
  xml: "file-xml",
  zip: "file-zip",
} as const satisfies Record<FileIconKind, FileIconName>;

const EXTENSION_KIND_MAP = {
  "7z": "zip",
  aac: "audio",
  ai: "image",
  avi: "video",
  bash: "code",
  bmp: "image",
  c: "code",
  cpp: "code",
  cs: "code",
  css: "code",
  csv: "csv",
  doc: "word",
  docx: "word",
  env: "code",
  flac: "audio",
  gif: "gif",
  go: "code",
  gz: "zip",
  h: "code",
  html: "code",
  java: "code",
  jpeg: "jpg",
  jpg: "jpg",
  js: "code",
  json: "code",
  jsx: "code",
  m4a: "audio",
  markdown: "text",
  md: "text",
  mkv: "video",
  mov: "video",
  mp3: "audio",
  mp4: "video",
  ogg: "audio",
  pdf: "pdf",
  php: "code",
  png: "png",
  ppt: "powerpoint",
  pptx: "powerpoint",
  py: "code",
  rar: "zip",
  rb: "code",
  rs: "code",
  sh: "code",
  sql: "code",
  svg: "svg",
  tar: "zip",
  ts: "code",
  tsx: "code",
  txt: "text",
  wav: "audio",
  webm: "video",
  webp: "image",
  xls: "excel",
  xlsx: "excel",
  xml: "xml",
  yaml: "code",
  yml: "code",
  zip: "zip",
} as const satisfies Record<string, FileIconKind>;

const MIME_KIND_MATCHERS: Array<{
  kind: FileIconKind;
  matches: (mimeType: string) => boolean;
}> = [
  { kind: "svg", matches: (mimeType) => mimeType.startsWith("image/svg") },
  { kind: "png", matches: (mimeType) => mimeType === "image/png" },
  { kind: "jpg", matches: (mimeType) => mimeType === "image/jpeg" || mimeType === "image/jpg" },
  { kind: "gif", matches: (mimeType) => mimeType === "image/gif" },
  { kind: "image", matches: (mimeType) => mimeType.startsWith("image/") },
  { kind: "audio", matches: (mimeType) => mimeType.startsWith("audio/") },
  { kind: "video", matches: (mimeType) => mimeType.startsWith("video/") },
  { kind: "pdf", matches: (mimeType) => mimeType === "application/pdf" },
  {
    kind: "word",
    matches: (mimeType) => mimeType.includes("wordprocessingml") || mimeType.includes("msword"),
  },
  {
    kind: "powerpoint",
    matches: (mimeType) => mimeType.includes("presentation") || mimeType.includes("powerpoint"),
  },
  {
    kind: "excel",
    matches: (mimeType) => mimeType.includes("spreadsheet") || mimeType.includes("excel"),
  },
  { kind: "csv", matches: (mimeType) => mimeType.includes("csv") },
  { kind: "xml", matches: (mimeType) => mimeType.includes("xml") },
  {
    kind: "code",
    matches: (mimeType) =>
      mimeType === "application/json" ||
      mimeType.includes("javascript") ||
      mimeType.includes("typescript") ||
      mimeType.includes("yaml") ||
      mimeType === "application/sql",
  },
  { kind: "text", matches: (mimeType) => mimeType.startsWith("text/") },
  {
    kind: "zip",
    matches: (mimeType) =>
      mimeType.includes("zip") ||
      mimeType.includes("rar") ||
      mimeType.includes("7z") ||
      mimeType.includes("gzip") ||
      mimeType.includes("tar"),
  },
  { kind: "binary", matches: (mimeType) => mimeType === "application/octet-stream" },
];

function getFileExtension(fileName: string): string {
  const leafName = fileName.trim().split(/[\\/]/).pop() ?? "";
  const dotIndex = leafName.lastIndexOf(".");

  if (dotIndex < 0) return "";
  if (dotIndex === 0 && !leafName.slice(1).includes(".")) return leafName.slice(1).toLowerCase();

  return leafName.slice(dotIndex + 1).toLowerCase();
}

function getMimeTypeKind(mimeType: string): FileIconKind | null {
  const normalizedMimeType = mimeType.trim().toLowerCase();

  if (!normalizedMimeType) return null;

  return MIME_KIND_MATCHERS.find(({ matches }) => matches(normalizedMimeType))?.kind ?? null;
}

export function getFileIconKind(fileName = "", mimeType = ""): FileIconKind {
  const extension = getFileExtension(fileName);
  const extensionKind = EXTENSION_KIND_MAP[extension as keyof typeof EXTENSION_KIND_MAP];

  return extensionKind ?? getMimeTypeKind(mimeType) ?? "default";
}

export function getFileIconName(fileName = "", mimeType = ""): FileIconName {
  return FILE_ICON_BY_KIND[getFileIconKind(fileName, mimeType)];
}
